import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import OtpInput from 'react-otp-input';
import { FormattedMessage } from 'react-intl';

import useEnhancedCountdown from '../../hooks/useEnhancedCountdown';
import { AUTH_ERROR_CUSTOM_MESSAGE } from '../../constants/errorCodes';

import FormTitle from '../FormWrapper/FormTitle';
import FormText from '../FormWrapper/FormText';
import FormButton from '../FormWrapper/FormButton';

import './index.scss';

const countdownTime = 60 * 1000; // how much we have to wait before enabling user to send new email
const STORAGE_KEY = 'OTPResendTimeout';

const PhoneCodeForm = ({
  code,
  setCode,
  onClick,
  isLoading,
  resendVerificationCode,
  phone,
  setHidePhone,
  email,
  hookError
}) => {
  const [error, setError] = useState(null);
  const [disabled, setDisabled] = useState(false);
  // Initialize enhanced countdown hook
  const {
    timeLeft,
    startWithStorage,
    reset,
    clearStorage
  } = useEnhancedCountdown(countdownTime);

  useEffect(() => {
    if (timeLeft <= 0) {
      setDisabled(false);
      if (error) {
        setError(null);
      }
    } else {
      setDisabled(true);
    }
  }, [timeLeft, error]);

  useEffect(() => {
    // Start countdown with localStorage persistence
    startWithStorage(STORAGE_KEY, countdownTime);

    // Cleanup function - reset countdown on unmount to prevent memory leaks
    return () => {
      reset();
    };
  }, [startWithStorage, reset]);

  const handleOnChange = value => {
    setCode(value);
    if (error) {
      setError(null);
    }
  };

  const handleOnSubmit = e => {
    e.preventDefault();
    if (code.length < 6) {
      setError(AUTH_ERROR_CUSTOM_MESSAGE.VERIFICATION_CODE_TOO_SHORT);
    } else {
      onClick(code);
    }
  };

  const handleLinkClick = () => {
    resendVerificationCode(phone, email);
    startWithStorage(STORAGE_KEY, countdownTime);
    setDisabled(true);
    if (error) {
      setError(null);
    }
  };

  const handleBackButtonClick = () => {
    clearStorage(STORAGE_KEY);
    setHidePhone(false);
    // Reset the countdown when going back
    reset();
  };

  return (
    <form onSubmit={handleOnSubmit}>
      <FormTitle
        title='auth.enter-mobile-code'
        defaultTitle='Enter verification code'
      />
      <FormText
        text='auth.verification-code'
        defaultText='We sent you a SMS code to the number {phone}'
        values={{ phone: phone }}
      />
      <OtpInput
        value={code}
        onChange={handleOnChange}
        numInputs={6}
        renderInput={props => (
          <input {...props} type='tel' inputMode='numeric' pattern='[0-9]*' />
        )}
        inputStyle={'phone-code-form__input'}
        containerStyle='phone-code-form__input-wrapper'
        placeholder='000000'
      />
      {(error || hookError) && (
        <span className='phone-code__error'>
          <FormattedMessage id={error || hookError} defaultMessage='Error' />
        </span>
      )}
      <div className='phone-code__text'>
        <FormattedMessage
          id='auth.code-not-received'
          defaultMessage="Didn't receive code?"
        />
        <button
          className='phone-code__button'
          disabled={disabled}
          onClick={handleLinkClick}
          type='button'
        >
          {disabled ? (
            <>
              <FormattedMessage
                id='auth.resend-in'
                defaultMessage='Resend in '
              />
              {Math.floor(timeLeft / 1000)}
              <FormattedMessage id='auth.seconds' defaultMessage=' seconds' />
            </>
          ) : (
            <FormattedMessage
              id='auth.send-again'
              defaultMessage='Send again'
            />
          )}
        </button>
      </div>
      <div
        className={cx('phone-code__buttons-wrapper', {
          'phone-code__buttons-wrapper--hasBackButton': !!setHidePhone
        })}
      >
        {!!setHidePhone && (
          <FormButton
            className='phone-code__back-button'
            text='auth.back'
            defaultText='Back'
            onClick={handleBackButtonClick}
            disabled={isLoading}
            backArrow
          />
        )}
        <FormButton
          className='phone-code__finish-button'
          text='auth.finish'
          defaultText='Finish'
          submit
          isLoading={isLoading}
          disabled={!!error}
        />
      </div>
    </form>
  );
};

PhoneCodeForm.propTypes = {
  code: PropTypes.string,
  setCode: PropTypes.func,
  isLoading: PropTypes.bool,
  activateRecaptcha: PropTypes.func,
  resendVerificationCode: PropTypes.func,
  phone: PropTypes.string,
  onClick: PropTypes.func,
  setHidePhone: PropTypes.func,
  onBackButtonClick: PropTypes.func,
  email: PropTypes.string,
  hookError: PropTypes.string
};

export default PhoneCodeForm;